import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import Breadcrumb from '../../components/common/Breadcrumb'
import {
    IconUser, IconPhone, IconUsers, IconBriefcase, IconBuilding,
    IconChevronDown, IconChevronLeft, IconLoader2, IconFolders, IconPlus, IconTrash,
    IconLock, IconEye, IconEyeOff, IconCreditCard, IconPencil, IconLink, IconX
} from '@tabler/icons-react'
import {
    relationships,
    roles,
    supervisors,
} from './employeeData'
import { JOB_TITLES } from '../../components/common/jobTitles'


// Hooks
import { useCreateEmployee, useUpdateEmployee, useEmployee, useLineManagerOptions, useOrgRoles, useCrossOrgEmployees, useCrossOrgAccess } from '../../hooks/useEmployees'
import { useUser } from '../../hooks/useUsers'
import { useCountries, useStates, useLgas } from '../../hooks/useGeoUnits'
import { useDepartments, useUnits } from '../../hooks/useDepartments'
import { useProjects } from '../../hooks/useProjects'
import { useAuth } from '../../contexts/AuthContext'

// Validation helper functions
const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

const validatePhone = (phone) => {
    const phoneRegex = /^\+?[0-9]{1,4}[\s\-]?(\(?\d{1,4}\)?[\s\-]?){1,4}\d{1,9}$/
    return phoneRegex.test(phone.trim())
}

// Gender options
const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
]

// Employment status options
const employmentStatusOptions = [
    { value: 'probation', label: 'Probation' },
    { value: 'active', label: 'Active' },
    { value: 'terminated', label: 'Terminated' },
    { value: 'resigned', label: 'Resigned' },
    { value: 'suspended', label: 'Suspended' },
]

// Initial form data structure matching API
const initialFormData = {
    // Personal Info
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',

    // Contact Details
    work_email: '',
    personal_email: '',
    phone_number: '',
    address: '',
    country: '',
    state: '',
    lga: '',

    // State of Origin
    state_of_origin: '',
    local_govt_origin: '',

    // Next of Kin
    next_of_kin_name: '',
    next_of_kin_relationship: '',
    next_of_kin_phone_no: '',
    next_of_kin_address: '',

    // Employment Details
    employee_no: '',
    job_title: '',
    grade_level: '',
    hire_date: '',
    contract_end_date: '',
    employment_status: 'probation',
    role_code: '',

    // Financial Information
    bank_name: '',
    bank_account_number: '',
    bank_account_name: '',
    pfa_name: '',
    pfa_number: '',
    tax_identification_number: '',
    tax_authority: '',

    // Assignments
    primary_unit: '',
    department: '',
    state_unit: '',
    line_manager_employee: '',
    cross_org_employee: '',

    // Account
    password: '',
    confirm_password: '',
}

export default function AddEmployee() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditMode = Boolean(id)
    const { user } = useAuth()
    const orgId = user?.orgId || localStorage.getItem('orgId')

    const [formData, setFormData] = useState(initialFormData)
    const [errors, setErrors] = useState({})
    const [touched, setTouched] = useState({})
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    // Project Assignments
    const [projectAssignments, setProjectAssignments] = useState([])
    const [loeError, setLoeError] = useState('')
    const [editingProjectIndex, setEditingProjectIndex] = useState(null)

    // Line Manager search
    const [lmSearch, setLmSearch] = useState('')
    const [lmSearchDebounced, setLmSearchDebounced] = useState('')
    const [lmDropdownOpen, setLmDropdownOpen] = useState(false)
    const [lmSelectedLabel, setLmSelectedLabel] = useState('')

    // Cross Organization assignment
    const [showCrossOrg, setShowCrossOrg] = useState(false)
    const [crossOrgSearch, setCrossOrgSearch] = useState('')
    const [crossOrgSearchDebounced, setCrossOrgSearchDebounced] = useState('')
    const [crossOrgDropdownOpen, setCrossOrgDropdownOpen] = useState(false)
    const [crossOrgSelectedLabel, setCrossOrgSelectedLabel] = useState('')
    const [currentProject, setCurrentProject] = useState({
        project_id: '',
        default_loe_percent: '',
        role_on_project: '',
        allocation_start: '',
        allocation_end: '',
        is_primary: false
    })

    // API Mutations
    const createEmployee = useCreateEmployee()
    const updateEmployee = useUpdateEmployee()
    const grantCrossOrgAccess = useCrossOrgAccess()

    // Fetch employee data if editing
    const { data: employeeData, isLoading: isLoadingEmployee } = useEmployee(id, orgId)

    // Fetch linked user to get role_code when editing
    const { data: linkedUser } = useUser(isEditMode ? employeeData?.user : null, orgId)

    // Geo data hooks
    const { data: countries = [], isLoading: loadingCountries } = useCountries()
    const { data: states = [], isLoading: loadingStates } = useStates(formData.country)
    const { data: lgas = [], isLoading: loadingLgas } = useLgas(formData.state)

    // State of origin uses same country, different state
    const { data: originStates = [] } = useStates(formData.country)
    const { data: originLgas = [] } = useLgas(formData.state_of_origin)

    // Roles
    const { data: rolesData, isLoading: loadingRoles, isError: rolesError } = useOrgRoles(orgId)
    const availableRoles = rolesData?.results || rolesData || []

    // Departments
    const { data: deptData, isLoading: loadingDepts } = useDepartments(orgId)
    const departments = deptData?.results || deptData || []

    // Units
    const { data: unitsData, isLoading: loadingUnits } = useUnits(formData.department, orgId)
    const rawUnits = unitsData?.results || unitsData || []

    // Explicitly filter units client-side to be safe
    const units = useMemo(() => {
        if (!formData.department) return [];
        return rawUnits.filter(u => String(u.department) === String(formData.department));
    }, [rawUnits, formData.department]);

    // Projects
    const { data: projectsData, isLoading: loadingProjects } = useProjects(orgId)
    const projects = projectsData?.results || projectsData || []


    // Resolve Role ID from selected Code for API
    const selectedRole = useMemo(() => {
        return availableRoles.find(r => r.code === formData.role_code);
    }, [formData.role_code, availableRoles]);

    // Fetch Line Manager Options from API
    // The API handles rank logic: finds employees with higher rank (lower number)
    const lmParams = {
        roleId: selectedRole?.id,
        departmentId: formData.department,
        unitId: formData.primary_unit,
        orgId,
        search: lmSearchDebounced
    };

    const { data: lineManagerOptions, isLoading: loadingLineManagers } = useLineManagerOptions(lmParams);
    const filteredLineManagers = lineManagerOptions || [];

    // Cross-org employees
    const { data: crossOrgData, isLoading: loadingCrossOrg } = useCrossOrgEmployees({ orgId, search: crossOrgSearchDebounced, enabled: showCrossOrg });
    const crossOrgEmployees = crossOrgData || [];

    // Debounce line manager search
    useEffect(() => {
        const timer = setTimeout(() => setLmSearchDebounced(lmSearch), 400)
        return () => clearTimeout(timer)
    }, [lmSearch])

    // Debounce cross-org search
    useEffect(() => {
        const timer = setTimeout(() => setCrossOrgSearchDebounced(crossOrgSearch), 400)
        return () => clearTimeout(timer)
    }, [crossOrgSearch])

    // Load employee data if editing
    useEffect(() => {
        if (isEditMode && employeeData) {
            setFormData({
                first_name: employeeData.first_name || '',
                middle_name: employeeData.middle_name || '',
                last_name: employeeData.last_name || '',
                gender: employeeData.gender || '',
                date_of_birth: employeeData.date_of_birth || '',
                work_email: employeeData.work_email || '',
                personal_email: employeeData.personal_email || '',
                phone_number: employeeData.phone_number || '',
                address: employeeData.address || '',
                country: employeeData.country || '',
                state: employeeData.state || '',
                lga: employeeData.lga || '',
                state_of_origin: employeeData.state_of_origin || '',
                local_govt_origin: employeeData.local_govt_origin || '',
                next_of_kin_name: employeeData.next_of_kin_name || '',
                next_of_kin_relationship: employeeData.next_of_kin_relationship || '',
                next_of_kin_phone_no: employeeData.next_of_kin_phone_no || '',
                next_of_kin_address: employeeData.next_of_kin_address || '',
                employee_no: employeeData.employee_no || '',
                job_title: employeeData.job_title || '',
                grade_level: employeeData.grade_level || '',
                hire_date: employeeData.hire_date || '',
                contract_end_date: employeeData.contract_end_date || '',
                employment_status: employeeData.employment_status || 'probation',
                role_code: linkedUser?.roles?.[0]?.code || employeeData.role_code || '',
                bank_name: employeeData.bank_name || '',
                bank_account_number: employeeData.bank_account_number || '',
                bank_account_name: employeeData.bank_account_name || '',
                pfa_name: employeeData.pfa_name || '',
                pfa_number: employeeData.pfa_number || '',
                tax_identification_number: employeeData.tax_identification_number || '',
                tax_authority: employeeData.tax_authority || '',
                primary_unit: employeeData.primary_unit || '',
                department: employeeData.department || '',
                state_unit: employeeData.state_unit || '',
                line_manager_employee: employeeData.line_manager_employee || '',
                cross_org_employee: employeeData.cross_org_employee || '',
            })
            if (employeeData.cross_org_employee) setShowCrossOrg(true)

            // Pre-populate project assignments from existing allocations
            if (employeeData.project_allocations?.length > 0) {
                setProjectAssignments(
                    employeeData.project_allocations.map(pa => ({
                        project_id: pa.project,
                        project_name: pa.project_name || '',
                        role_on_project: pa.role_on_project || '',
                        allocation_start: pa.allocation_start || '',
                        allocation_end: pa.allocation_end || '',
                        default_loe_percent: pa.default_loe_percent || '',
                        is_primary: pa.is_primary || false,
                    }))
                )
            }
        }
    }, [isEditMode, employeeData, linkedUser])

    // Patch role_code once linkedUser resolves (handles async load after form population)
    useEffect(() => {
        if (isEditMode && linkedUser?.roles?.[0]?.code) {
            setFormData(prev => ({ ...prev, role_code: linkedUser.roles[0].code }))
        }
    }, [isEditMode, linkedUser])

    // Patch lmSelectedLabel once line manager options load in edit mode
    useEffect(() => {
        if (isEditMode && formData.line_manager_employee && !lmSelectedLabel && filteredLineManagers.length > 0) {
            const manager = filteredLineManagers.find(m => String(m.id) === String(formData.line_manager_employee))
            if (manager) {
                setLmSelectedLabel(`${manager.name}${manager.role ? ` (${manager.role})` : ''}`)
            }
        }
    }, [isEditMode, formData.line_manager_employee, filteredLineManagers, lmSelectedLabel])

    // Update form data
    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        setTouched(prev => ({ ...prev, [field]: true }))

        // Clear dependent fields when parent changes
        if (field === 'country') {
            setFormData(prev => ({ ...prev, state: '', lga: '', state_of_origin: '', local_govt_origin: '' }))
        }
        if (field === 'state') {
            setFormData(prev => ({ ...prev, lga: '' }))
        }
        if (field === 'state_of_origin') {
            setFormData(prev => ({ ...prev, local_govt_origin: '' }))
        }
        if (field === 'department') {
            setFormData(prev => ({ ...prev, primary_unit: '' }))
        }
    }

    // Project assignment helpers
    const updateProjectField = (field, value) => {
        setCurrentProject(prev => ({ ...prev, [field]: value }))
    }

    const resetProjectForm = () => {
        setCurrentProject({ project_id: '', default_loe_percent: '', role_on_project: '', allocation_start: '', allocation_end: '', is_primary: false })
        setEditingProjectIndex(null)
        setLoeError('')
    }

    const handleEditProjectAssignment = (index) => {
        setEditingProjectIndex(index)
        setCurrentProject({ ...projectAssignments[index] })
        setLoeError('')
    }

    const handleAddProjectAssignment = () => {
        if (!currentProject.project_id) {
            toast.error('Please select a project')
            return
        }

        // Check for duplicate only when adding new
        if (editingProjectIndex === null && projectAssignments.some(p => p.project_id === currentProject.project_id)) {
            toast.error('This project is already assigned')
            return
        }

        // Validate LOE
        if (currentProject.default_loe_percent === '' || currentProject.default_loe_percent === undefined) {
            setLoeError('Default LOE is required')
            return
        }
        const loe = parseFloat(currentProject.default_loe_percent)
        if (isNaN(loe) || loe < 0 || loe > 100) {
            setLoeError('LOE must be between 0 and 100')
            return
        }

        const selectedProject = projects.find(p => String(p.id) === String(currentProject.project_id))
        const updated = {
            ...currentProject,
            project_name: selectedProject?.name || currentProject.project_name || 'Unknown Project'
        }

        if (editingProjectIndex !== null) {
            // Update existing
            setProjectAssignments(prev => prev.map((p, i) => i === editingProjectIndex ? updated : p))
            toast.success('Project assignment updated')
        } else {
            // Add new — first project is primary by default
            updated.is_primary = projectAssignments.length === 0 ? true : currentProject.is_primary
            setProjectAssignments(prev => [...prev, updated])
            toast.success('Project assignment added')
        }

        resetProjectForm()
    }

    const handleRemoveProjectAssignment = (index) => {
        setProjectAssignments(prev => prev.filter((_, i) => i !== index))
        if (editingProjectIndex === index) resetProjectForm()
        toast.success('Project assignment removed')
    }

    // Validate form
    const validateForm = () => {
        const newErrors = {}

        // Required fields
        if (!formData.first_name || formData.first_name.length < 2) {
            newErrors.first_name = 'First name is required (min 2 characters)'
        }
        if (!formData.last_name || formData.last_name.length < 2) {
            newErrors.last_name = 'Last name is required (min 2 characters)'
        }
        if (!formData.work_email) {
            newErrors.work_email = 'Work email is required'
        } else if (!validateEmail(formData.work_email)) {
            newErrors.work_email = 'Invalid email format'
        }
        if (!formData.phone_number) {
            newErrors.phone_number = 'Phone number is required'
        } else if (!validatePhone(formData.phone_number)) {
            newErrors.phone_number = 'Invalid phone number (e.g. +234 800 000 0000)'
        }
        if (!formData.employee_no) {
            newErrors.employee_no = 'Employee number is required'
        }
        if (!formData.job_title) {
            newErrors.job_title = 'Job title is required'
        }
        if (!formData.hire_date) {
            newErrors.hire_date = 'Hire date is required'
        }
        if (!formData.gender) {
            newErrors.gender = 'Gender is required'
        }
        if (!formData.employment_status) {
            newErrors.employment_status = 'Employment status is required'
        }
        if (!formData.role_code && !rolesError) {
            newErrors.role_code = 'Role is required'
        }

        // Location fields
        if (!formData.country) {
            newErrors.country = 'Country is required'
        }
        if (!formData.state) {
            newErrors.state = 'State is required'
        }
        if (!formData.lga) {
            newErrors.lga = 'LGA is required'
        }

        // Contract end date
        if (!formData.contract_end_date) {
            newErrors.contract_end_date = 'Contract end date is required'
        }

        // Financial details
        if (!formData.bank_name) {
            newErrors.bank_name = 'Bank name is required'
        }
        if (!formData.bank_account_number) {
            newErrors.bank_account_number = 'Account number is required'
        }
        if (!formData.bank_account_name) {
            newErrors.bank_account_name = 'Account name is required'
        }
        if (!formData.pfa_name) {
            newErrors.pfa_name = 'PFA name is required'
        }
        if (!formData.pfa_number) {
            newErrors.pfa_number = 'PFA number is required'
        }

        // Next of kin phone required if name provided
        if (formData.next_of_kin_name && !formData.next_of_kin_phone_no) {
            newErrors.next_of_kin_phone_no = 'Phone is required when name is provided'
        }

        // Password (create mode only)
        if (!isEditMode) {
            if (!formData.password) {
                newErrors.password = 'Password is required'
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters'
            }
            if (!formData.confirm_password) {
                newErrors.confirm_password = 'Please confirm the password'
            } else if (formData.password !== formData.confirm_password) {
                newErrors.confirm_password = 'Passwords do not match'
            }
        }

        setErrors(newErrors)
        if (Object.keys(newErrors).length > 0) {
            console.error('Validation failed on these fields:', newErrors)
        }
        // Return both the boolean and the errors object for immediate use
        return { valid: Object.keys(newErrors).length === 0, errors: newErrors }
    }

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault()

        // Touch ALL validated fields so red text shows immediately on submit
        const allValidatedFields = [
            'first_name', 'last_name', 'gender', 'work_email', 'phone_number',
            'employee_no', 'job_title', 'hire_date', 'employment_status',
            'country', 'state', 'lga', 'contract_end_date',
            'bank_name', 'bank_account_number', 'bank_account_name',
            'pfa_name', 'pfa_number',
            ...(!rolesError ? ['role_code'] : []),
            ...(!isEditMode ? ['password', 'confirm_password'] : [])
        ]
        const newTouched = {}
        allValidatedFields.forEach(field => { newTouched[field] = true })
        setTouched(prev => ({ ...prev, ...newTouched }))

        const { valid, errors: validationErrors } = validateForm()
        if (!valid) {
            const firstErrorField = Object.keys(validationErrors)[0]
            const firstErrorMsg = validationErrors[firstErrorField]
            toast.error(firstErrorMsg || 'Please fill in all required fields')
            // Scroll to the first invalid field
            setTimeout(() => {
                const el = document.querySelector(`[name="${firstErrorField}"], [data-field="${firstErrorField}"]`)
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }, 100)
            return
        }

        // Prepare payload - only send non-empty values
        const payload = {}
        Object.keys(formData).forEach(key => {
            const value = formData[key]
            // confirm_password never sent to API
            if (key === 'confirm_password') return
            if (value !== '' && value !== null && value !== undefined) {
                if (key === 'primary_unit') {
                    payload[isEditMode ? 'primary_unit' : 'primary_unit_id'] = value
                } else if (key === 'department') {
                    payload[isEditMode ? 'department' : 'department_id'] = value
                } else if (key === 'state_unit') {
                    payload[isEditMode ? 'state_unit' : 'state_unit_id'] = value
                } else if (key === 'line_manager_employee') {
                    payload[isEditMode ? 'line_manager_employee' : 'line_manager_employee_id'] = value
                } else if (key === 'cross_org_employee') {
                    // handled separately via cross-org-access endpoint — skip from main payload
                } else {
                    payload[key] = value
                }
            }
        })

        // Add project assignments to payload
        // In edit mode always send the array (even empty) so backend can sync removals
        if (isEditMode || projectAssignments.length > 0) {
            payload['projects'] = projectAssignments.map(pa => ({
                project_id: pa.project_id,
                role_on_project: pa.role_on_project || null,
                allocation_start: pa.allocation_start || null,
                allocation_end: pa.allocation_end || null,
                default_loe_percent: pa.default_loe_percent ? parseFloat(pa.default_loe_percent) : null,
                is_primary: pa.is_primary || false
            }))
        }

        try {
            if (isEditMode) {
                console.log('UPDATE PAYLOAD:', payload)
                const updateResponse = await updateEmployee.mutateAsync({
                    id,
                    data: payload,
                    orgId
                })
                console.log('UPDATE RESPONSE:', updateResponse)
                toast.success('Employee updated successfully')
            } else {
                const response = await createEmployee.mutateAsync({
                    data: payload,
                    orgId
                })

                // If a cross-org employee was selected, grant cross-org access before showing success
                if (formData.cross_org_employee) {
                    const newEmployeeId = response?.id || response?.employee?.id || response?.employee_id
                    await grantCrossOrgAccess.mutateAsync({
                        managerEmployeeId: formData.cross_org_employee,
                        reportEmployeeId: newEmployeeId,
                        orgId,
                    })
                }

                toast.success(response.message || 'Employee created successfully')

                // Show temporary password info if available
                if (response.temporary_password) {
                    toast.success(`Temporary password sent to ${formData.work_email}`, { duration: 5000 })
                }
            }
            setTimeout(() => navigate('/employee'), 1000)
        } catch (error) {
            toast.error(error.message || 'Failed to save employee')
        }
    }

    // check if field has error
    const hasError = (field) => touched[field] && errors[field]

    const isSubmitting = createEmployee.isPending || updateEmployee.isPending || grantCrossOrgAccess.isPending

    if (isEditMode && isLoadingEmployee) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <IconLoader2 className="w-8 h-8 animate-spin text-org-blue-primary" />
            </div>
        )
    }

    return (
        <>
            <Breadcrumb
                title={isEditMode ? 'Edit Employee' : 'Add Employee'}
                path={`Employees / ${isEditMode ? 'Edit Employee' : 'Add Employee'}`}
            />
            {/* Back button */}
            <div className="px-20">
                <button
                    type="button"
                    onClick={() => navigate('/employee')}
                    className="btn btn-outline-secondary flex items-center gap-5 mb-15"
                >
                    <IconChevronLeft size={16} />
                    Back
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="space-y-20 p-20">
                    {/* Personal Information */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-org-blue-primary px-16 py-8">
                            <div className="flex items-center gap-11">
                                <div className="w-40 h-40 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/10">
                                    <IconUser size={22} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white leading-tight">
                                        Personal Information
                                    </h3>
                                    <p className="text-white text-xs">Basic personal details</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-16 bg-white">
                            {/* Name Row */}
                            <div className="grid md:grid-cols-12 gap-20 mb-20">
                                <div className="md:col-span-12">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-10">Name Information</h4>
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        First Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.first_name}
                                        onChange={(e) => updateField('first_name', e.target.value)}
                                        placeholder="Enter first name"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('first_name') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('first_name') && <span className="text-danger text-xs mt-4 block">{errors.first_name}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Middle Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.middle_name}
                                        onChange={(e) => updateField('middle_name', e.target.value)}
                                        placeholder="Enter middle name"
                                        className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                    />
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Last Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.last_name}
                                        onChange={(e) => updateField('last_name', e.target.value)}
                                        placeholder="Enter last name"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('last_name') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('last_name') && <span className="text-danger text-xs mt-4 block">{errors.last_name}</span>}
                                </div>
                            </div>

                            {/* Gender & DOB Row */}
                            <div className="grid md:grid-cols-12 gap-20">
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Gender <span className="text-danger">*</span></label>
                                    <div className="relative">
                                        <select
                                            value={formData.gender}
                                            onChange={(e) => updateField('gender', e.target.value)}
                                            className={`w-full px-12 py-10 rounded-lg border transition-all bg-white appearance-none ${hasError('gender') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                        >
                                            <option value="">Select gender</option>
                                            {genderOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                    {hasError('gender') && <span className="text-danger text-xs mt-4 block">{errors.gender}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Date of Birth</label>
                                    <input
                                        type="date"
                                        value={formData.date_of_birth}
                                        onChange={(e) => updateField('date_of_birth', e.target.value)}
                                        className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Details */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-org-blue-primary px-16 py-8">
                            <div className="flex items-center gap-12">
                                <div className="w-40 h-40 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/10">
                                    <IconPhone size={22} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white leading-tight">
                                        Contact Details
                                    </h3>
                                    <p className="text-white text-xs">Contact and address information</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-16 bg-white">
                            <div className="grid md:grid-cols-12 gap-20">
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Work Email <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.work_email}
                                        onChange={(e) => updateField('work_email', e.target.value)}
                                        placeholder="email@organization.com"
                                        autoComplete="off"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('work_email') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('work_email') && <span className="text-danger text-xs mt-4 block">{errors.work_email}</span>}
                                </div>
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Personal Email</label>
                                    <input
                                        type="email"
                                        value={formData.personal_email}
                                        onChange={(e) => updateField('personal_email', e.target.value)}
                                        placeholder="personal@email.com"
                                        autoComplete="off"
                                        className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                    />
                                </div>
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Phone Number <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.phone_number}
                                        onChange={(e) => updateField('phone_number', e.target.value)}
                                        placeholder="+234..."
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('phone_number') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('phone_number') && <span className="text-danger text-xs mt-4 block">{errors.phone_number}</span>}
                                </div>

                                {/* Address */}
                                <div className="form-group md:col-span-12">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Address</label>
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => updateField('address', e.target.value)}
                                        placeholder="123 Street Name"
                                        className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                    />
                                </div>

                                {/* Country */}
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Country <span className="text-danger">*</span></label>
                                    <div className="relative">
                                        <select
                                            value={formData.country}
                                            onChange={(e) => updateField('country', e.target.value)}
                                            className={`w-full px-12 py-10 rounded-lg border bg-white appearance-none transition-all ${hasError('country') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                            disabled={loadingCountries}
                                        >
                                            <option value="">Select country</option>
                                            {countries.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                    {hasError('country') && <span className="text-danger text-xs mt-4 block">{errors.country}</span>}
                                </div>

                                {/* State */}
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">State <span className="text-danger">*</span></label>
                                    <div className="relative">
                                        <select
                                            value={formData.state}
                                            onChange={(e) => updateField('state', e.target.value)}
                                            className={`w-full px-12 py-10 rounded-lg border bg-white appearance-none transition-all ${hasError('state') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                            disabled={!formData.country || loadingStates}
                                        >
                                            <option value="">Select state</option>
                                            {states.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                    {hasError('state') && <span className="text-danger text-xs mt-4 block">{errors.state}</span>}
                                </div>

                                {/* LGA */}
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">LGA <span className="text-danger">*</span></label>
                                    <div className="relative">
                                        <select
                                            value={formData.lga}
                                            onChange={(e) => updateField('lga', e.target.value)}
                                            className={`w-full px-12 py-10 rounded-lg border bg-white appearance-none transition-all ${hasError('lga') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                            disabled={!formData.state || loadingLgas}
                                        >
                                            <option value="">Select LGA</option>
                                            {lgas.map(l => (
                                                <option key={l.id} value={l.id}>{l.name}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                    {hasError('lga') && <span className="text-danger text-xs mt-4 block">{errors.lga}</span>}
                                </div>
                            </div>

                            {/* State of Origin Section */}
                            <div className="mt-20">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-10">State of Origin</h4>
                                <div className="grid md:grid-cols-12 gap-20">
                                    <div className="form-group md:col-span-6">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">State of Origin</label>
                                        <div className="relative">
                                            <select
                                                value={formData.state_of_origin}
                                                onChange={(e) => updateField('state_of_origin', e.target.value)}
                                                className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white appearance-none"
                                                disabled={!formData.country}
                                            >
                                                <option value="">Select state of origin</option>
                                                {originStates.map(s => (
                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                ))}
                                            </select>
                                            <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div className="form-group md:col-span-6">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">LGA of Origin</label>
                                        <div className="relative">
                                            <select
                                                value={formData.local_govt_origin}
                                                onChange={(e) => updateField('local_govt_origin', e.target.value)}
                                                className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white appearance-none"
                                                disabled={!formData.state_of_origin}
                                            >
                                                <option value="">Select LGA of origin</option>
                                                {originLgas.map(l => (
                                                    <option key={l.id} value={l.id}>{l.name}</option>
                                                ))}
                                            </select>
                                            <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Next of Kin */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-org-blue-primary px-16 py-8">
                            <div className="flex items-center gap-12">
                                <div className="w-40 h-40 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/10">
                                    <IconUsers size={22} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white leading-tight">
                                        Next of Kin
                                    </h3>
                                    <p className="text-white text-xs">Emergency contact information</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-16 bg-white">
                            <div className="grid md:grid-cols-12 gap-20">
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Full Name</label>
                                    <input
                                        type="text"
                                        value={formData.next_of_kin_name}
                                        onChange={(e) => updateField('next_of_kin_name', e.target.value)}
                                        placeholder="Enter full name"
                                        className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                    />
                                </div>
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Relationship</label>
                                    <div className="relative">
                                        <select
                                            value={formData.next_of_kin_relationship}
                                            onChange={(e) => updateField('next_of_kin_relationship', e.target.value)}
                                            className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white appearance-none"
                                        >
                                            <option value="">Select relationship</option>
                                            {relationships.map(rel => (
                                                <option key={rel.id} value={rel.name}>{rel.name}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Phone {formData.next_of_kin_name && <span className="text-danger">*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.next_of_kin_phone_no}
                                        onChange={(e) => updateField('next_of_kin_phone_no', e.target.value)}
                                        placeholder="+234..."
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('next_of_kin_phone_no') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('next_of_kin_phone_no') && <span className="text-danger text-xs mt-4 block">{errors.next_of_kin_phone_no}</span>}
                                </div>
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Address</label>
                                    <input
                                        type="text"
                                        value={formData.next_of_kin_address}
                                        onChange={(e) => updateField('next_of_kin_address', e.target.value)}
                                        placeholder="Enter address"
                                        className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Employment Details */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-org-blue-primary px-16 py-8">
                            <div className="flex items-center gap-12">
                                <div className="w-40 h-40 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/10">
                                    <IconBriefcase size={22} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white leading-tight">
                                        Employment Details
                                    </h3>
                                    <p className="text-white text-xs">Work information and role</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-16 bg-white">
                            <div className="grid md:grid-cols-12 gap-20">
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Employee Number <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.employee_no}
                                        onChange={(e) => updateField('employee_no', e.target.value.toUpperCase())}
                                        placeholder="e.g., EMP-001"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all uppercase ${hasError('employee_no') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('employee_no') && <span className="text-danger text-xs mt-4 block">{errors.employee_no}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Job Title <span className="text-danger">*</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData.job_title}
                                            onChange={(e) => updateField('job_title', e.target.value)}
                                            className={`w-full px-12 py-10 rounded-lg border transition-all bg-white appearance-none ${hasError('job_title') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                        >
                                            <option value="">Select job title</option>
                                            {JOB_TITLES.map(jt => (
                                                <option key={jt.id} value={jt.label}>{jt.label}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                    {hasError('job_title') && <span className="text-danger text-xs mt-4 block">{errors.job_title}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Grade Level</label>
                                    <input
                                        type="text"
                                        value={formData.grade_level}
                                        onChange={(e) => updateField('grade_level', e.target.value)}
                                        placeholder="e.g., Level 3"
                                        className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                    />
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Hire Date <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.hire_date}
                                        onChange={(e) => updateField('hire_date', e.target.value)}
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('hire_date') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('hire_date') && <span className="text-danger text-xs mt-4 block">{errors.hire_date}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Contract End Date <span className="text-danger">*</span></label>
                                    <input
                                        type="date"
                                        value={formData.contract_end_date}
                                        onChange={(e) => updateField('contract_end_date', e.target.value)}
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('contract_end_date') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('contract_end_date') && <span className="text-danger text-xs mt-4 block">{errors.contract_end_date}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Employment Status <span className="text-danger">*</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData.employment_status}
                                            onChange={(e) => updateField('employment_status', e.target.value)}
                                            className={`w-full px-12 py-10 rounded-lg border transition-all bg-white appearance-none ${hasError('employment_status') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                        >
                                            <option value="">Select status</option>
                                            {employmentStatusOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                    {hasError('employment_status') && <span className="text-danger text-xs mt-4 block">{errors.employment_status}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                        Role <span className="text-danger">*</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData.role_code}
                                            onChange={(e) => updateField('role_code', e.target.value)}
                                            disabled={loadingRoles || rolesError}
                                            className={`w-full px-12 py-10 rounded-lg border transition-all bg-white appearance-none ${hasError('role_code') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                        >
                                            <option value="">{rolesError ? 'No roles available' : 'Select role'}</option>
                                            {availableRoles.map(role => (
                                                <option key={role.id} value={role.code}>{role.name}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                    {rolesError && <span className="text-red-600 text-xs mt-4 block">You don't have permission to list roles.</span>}
                                    {hasError('role_code') && <span className="text-danger text-xs mt-4 block">{errors.role_code}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Project Assignment Section */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-org-blue-primary px-16 py-8">
                            <div className="flex items-center gap-12">
                                <div className="w-40 h-40 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/10">
                                    <IconFolders size={22} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white leading-tight">
                                        Project Assignment
                                    </h3>
                                    <p className="text-white text-xs">Assign employee to projects</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-16 bg-white">
                            {/* Saved Project Assignments */}
                            {projectAssignments.length > 0 && (
                                <div className="mb-20">
                                    <div className="flex items-center justify-between mb-12">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Assigned Projects</h4>
                                        <span className="text-xs font-semibold text-org-blue-primary bg-org-blue-primary/10 px-8 py-3 rounded-full">
                                            {projectAssignments.length} project{projectAssignments.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <div className="flex flex-col gap-8">
                                        {projectAssignments.map((assignment, index) => {
                                            const loe = parseFloat(assignment.default_loe_percent) || 0;
                                            const accent = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#7c3aed'][index % 5];
                                            return (
                                                <div key={index} className="flex items-center gap-10 px-12 py-8 rounded-lg border border-gray-200 bg-gray-50" style={{ borderLeftColor: accent, borderLeftWidth: 3 }}>
                                                    <IconFolders size={14} style={{ color: accent }} className="flex-shrink-0" />

                                                    {/* Project name */}
                                                    <div className="flex items-center gap-6 w-[35%]">
                                                        <span className="text-xs font-semibold text-gray-800 truncate">{assignment.project_name}</span>
                                                        {assignment.is_primary && (
                                                            <span className="text-[9px] font-bold px-5 py-1 rounded-full text-white flex-shrink-0" style={{ backgroundColor: accent }}>Primary</span>
                                                        )}
                                                    </div>

                                                    {/* Role */}
                                                    <div className="flex-1 text-[10px]">
                                                        <span className="text-gray-400">Role: </span>
                                                        <span className="text-gray-700 font-medium">{assignment.role_on_project || '—'}</span>
                                                    </div>

                                                    {/* LOE */}
                                                    <div className="flex-1 text-[10px]">
                                                        <span className="text-gray-400">LOE: </span>
                                                        <span className="font-semibold" style={{ color: accent }}>{loe}%</span>
                                                    </div>

                                                    {/* Period */}
                                                    <div className="flex-1 text-[10px]">
                                                        <span className="text-gray-400">Period: </span>
                                                        <span className="text-gray-700">{assignment.allocation_start || '—'} → {assignment.allocation_end || 'Ongoing'}</span>
                                                    </div>

                                                    <div className="flex items-center gap-4 flex-shrink-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleEditProjectAssignment(index)}
                                                            className="p-4 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                            title="Edit"
                                                        >
                                                            <IconPencil size={13} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveProjectAssignment(index)}
                                                            className="p-4 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            title="Remove"
                                                        >
                                                            <IconTrash size={13} />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Add / Edit Project Form */}
                            <div className={projectAssignments.length > 0 ? 'pt-15 border-t border-gray-200' : ''}>
                                <div className="flex items-center justify-between mb-10">
                                    {projectAssignments.length > 0 && (
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">
                                            {editingProjectIndex !== null ? 'Edit Project' : 'Add New Project'}
                                        </h4>
                                    )}
                                    {editingProjectIndex !== null && (
                                        <button type="button" onClick={resetProjectForm} className="text-xs text-gray-400 hover:text-gray-600 underline">
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                                <div className="grid md:grid-cols-12 gap-20">
                                    <div className="form-group md:col-span-4">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                            Project Name <span className="text-danger">*</span>
                                        </label>
                                        <div className="relative">
                                            <select
                                                value={currentProject.project_id}
                                                onChange={(e) => updateProjectField('project_id', e.target.value)}
                                                disabled={loadingProjects}
                                                className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white appearance-none"
                                            >
                                                <option value="">{loadingProjects ? 'Loading...' : 'Select project'}</option>
                                                {projects.map(project => (
                                                    <option key={project.id} value={project.id}>{project.name}</option>
                                                ))}
                                            </select>
                                            <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div className="form-group md:col-span-4">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">Default LOE (%) <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={currentProject.default_loe_percent}
                                            onChange={(e) => {
                                                const val = e.target.value
                                                updateProjectField('default_loe_percent', val)
                                                if (val !== '' && parseFloat(val) > 100) {
                                                    setLoeError('LOE cannot exceed 100%')
                                                } else {
                                                    setLoeError('')
                                                }
                                            }}
                                            placeholder="e.g., 80.00"
                                            className={`w-full px-12 py-10 rounded-lg border focus:ring-2 focus:ring-org-blue-primary/10 ${loeError ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-org-blue-primary'}`}
                                        />
                                        {loeError && <p className="text-red-500 text-xs mt-4">{loeError}</p>}
                                    </div>
                                    <div className="form-group md:col-span-4">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">Role on Project</label>
                                        <input
                                            type="text"
                                            value={currentProject.role_on_project}
                                            onChange={(e) => updateProjectField('role_on_project', e.target.value)}
                                            placeholder="e.g., Program Officer, Technical Support"
                                            className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                        />
                                    </div>
                                    <div className="form-group md:col-span-4">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">Allocation Start</label>
                                        <input
                                            type="date"
                                            value={currentProject.allocation_start}
                                            onChange={(e) => updateProjectField('allocation_start', e.target.value)}
                                            className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                        />
                                    </div>
                                    <div className="form-group md:col-span-4">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">Allocation End</label>
                                        <input
                                            type="date"
                                            value={currentProject.allocation_end}
                                            onChange={(e) => updateProjectField('allocation_end', e.target.value)}
                                            className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10"
                                        />
                                    </div>
                                    <div className="form-group md:col-span-12 flex items-center">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={currentProject.is_primary}
                                                onChange={(e) => updateProjectField('is_primary', e.target.checked)}
                                                className="w-14 h-14 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            {/* whitespace-nowrap ensures the text stays on one line */}
                                            <span className="text-gray-700 text-sm font-medium whitespace-nowrap">
                                                Set as Primary Project
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Add / Save Button */}
                                <div className="flex justify-end mt-15">
                                    <button
                                        type="button"
                                        onClick={handleAddProjectAssignment}
                                        className="btn btn-primary flex items-center gap-8"
                                    >
                                        {editingProjectIndex !== null ? <><IconPencil size={16} /> Save Changes</> : <><IconPlus size={16} /> Add Project</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Assignments Section */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border">
                        <div className="bg-org-blue-primary px-16 py-8 rounded-t-xl overflow-hidden">
                            <div className="flex items-center gap-12 text-white">
                                <div className="w-40 h-40 rounded-lg bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/10">
                                    <IconBuilding size={22} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold leading-tight">Assignments</h3>
                                    <p className="text-white text-xs font-medium">Reporting lines and organizational units</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-16 bg-white">
                            <div className="grid md:grid-cols-12 gap-20">

                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Department</label>
                                    <div className="relative">
                                        <select
                                            value={formData.department}
                                            onChange={(e) => updateField('department', e.target.value)}
                                            className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white appearance-none"
                                            disabled={loadingDepts}
                                        >
                                            <option value="">{loadingDepts ? 'Loading...' : 'Select department'}</option>
                                            {departments.map(dept => (
                                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>

                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm"> Unit</label>
                                    <div className="relative">
                                        <select
                                            value={formData.primary_unit}
                                            onChange={(e) => updateField('primary_unit', e.target.value)}
                                            className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white appearance-none"
                                            disabled={!formData.department || loadingUnits}
                                        >
                                            <option value="">{loadingUnits ? 'Loading...' : 'Select unit'}</option>
                                            {units.map(unit => (
                                                <option key={unit.id} value={unit.id}>{unit.name}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>

                                {/* <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">State Unit</label>
                                    <div className="relative">
                                        <select
                                            value={formData.state_unit}
                                            onChange={(e) => updateField('state_unit', e.target.value)}
                                            className="w-full px-12 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white appearance-none"
                                            disabled={loadingUnits}
                                        >
                                            <option value="">{loadingUnits ? 'Loading...' : 'Select state unit'}</option>
                                            {units.map(unit => (
                                                <option key={unit.id} value={unit.id}>{unit.name}</option>
                                            ))}
                                        </select>
                                        <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div> */}
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Line Manager</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={lmDropdownOpen ? lmSearch : lmSelectedLabel}
                                            onFocus={() => { setLmDropdownOpen(true); setLmSearch('') }}
                                            onBlur={() => setTimeout(() => setLmDropdownOpen(false), 150)}
                                            onChange={(e) => setLmSearch(e.target.value)}
                                            placeholder="Search line manager..."
                                            className="w-full px-12 pr-32 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white text-sm"
                                        />
                                        {formData.line_manager_employee && (
                                            <button
                                                type="button"
                                                onClick={() => { updateField('line_manager_employee', ''); setLmSelectedLabel(''); setLmSearch('') }}
                                                className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <IconX size={14} />
                                            </button>
                                        )}
                                        {lmDropdownOpen && (
                                            <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-2 max-h-48 overflow-y-auto">
                                                {loadingLineManagers ? (
                                                    <div className="px-12 py-10 text-sm text-gray-500 flex items-center gap-8">
                                                        <IconLoader2 size={14} className="animate-spin" /> Loading...
                                                    </div>
                                                ) : filteredLineManagers.length === 0 ? (
                                                    <div className="px-12 py-10 text-sm text-gray-400">
                                                        {lmSearch ? 'No results found' : 'Type to search line managers'}
                                                    </div>
                                                ) : (
                                                    filteredLineManagers.map(m => (
                                                        <div
                                                            key={m.id}
                                                            onMouseDown={() => {
                                                                updateField('line_manager_employee', m.id)
                                                                setLmSelectedLabel(`${m.name}${m.role ? ` (${m.role})` : ''}`)
                                                                setLmDropdownOpen(false)
                                                            }}
                                                            className={`px-12 py-10 cursor-pointer text-sm hover:bg-gray-50 ${String(formData.line_manager_employee) === String(m.id) ? 'bg-blue-50 text-org-blue-primary font-medium' : 'text-gray-700'}`}
                                                        >
                                                            <span className="font-medium">{m.name}</span>
                                                            {m.role && <span className="text-gray-400 ml-4">({m.role})</span>}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Cross Organization Assignment */}
                                {showCrossOrg && (
                                    <div className="form-group md:col-span-4">
                                        <label className="font-semibold text-gray-700 mb-6 flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-6">
                                                <IconLink size={14} className="text-org-blue-primary" />
                                                Cross Org Employee
                                                <span className="text-xs font-normal text-gray-400">(Optional)</span>
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => { setShowCrossOrg(false); updateField('cross_org_employee', ''); setCrossOrgSelectedLabel(''); setCrossOrgSearch('') }}
                                                className="text-xs text-gray-400 hover:text-danger flex items-center gap-4"
                                            >
                                                <IconX size={11} /> Remove
                                            </button>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={crossOrgDropdownOpen ? crossOrgSearch : crossOrgSelectedLabel}
                                                onFocus={() => { setCrossOrgDropdownOpen(true); setCrossOrgSearch('') }}
                                                onBlur={() => setTimeout(() => setCrossOrgDropdownOpen(false), 150)}
                                                onChange={(e) => setCrossOrgSearch(e.target.value)}
                                                placeholder="Search cross-org employee..."
                                                className="w-full px-12 pr-32 py-10 rounded-lg border border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10 bg-white text-sm"
                                            />
                                            {formData.cross_org_employee && (
                                                <button
                                                    type="button"
                                                    onClick={() => { updateField('cross_org_employee', ''); setCrossOrgSelectedLabel(''); setCrossOrgSearch('') }}
                                                    className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                >
                                                    <IconX size={14} />
                                                </button>
                                            )}
                                            {crossOrgDropdownOpen && (
                                                <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-2 max-h-48 overflow-y-auto">
                                                    {loadingCrossOrg ? (
                                                        <div className="px-12 py-10 text-sm text-gray-500 flex items-center gap-8">
                                                            <IconLoader2 size={14} className="animate-spin" /> Loading...
                                                        </div>
                                                    ) : crossOrgEmployees.length === 0 ? (
                                                        <div className="px-12 py-10 text-sm text-gray-400">No employees found</div>
                                                    ) : (
                                                        crossOrgEmployees.map(m => (
                                                            <div
                                                                key={m.id}
                                                                onMouseDown={() => {
                                                                    updateField('cross_org_employee', m.id)
                                                                    setCrossOrgSelectedLabel(`${m.name}${m.organization ? ` — ${m.organization}` : ''}`)
                                                                    setCrossOrgDropdownOpen(false)
                                                                }}
                                                                className={`px-12 py-10 cursor-pointer text-sm hover:bg-gray-50 ${String(formData.cross_org_employee) === String(m.id) ? 'bg-blue-50 text-org-blue-primary font-medium' : 'text-gray-700'}`}
                                                            >
                                                                <div className="font-medium">{m.name}</div>
                                                                {m.organization && <div className="text-gray-400 text-xs">{m.organization}</div>}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Assign Cross Organization button */}
                            {!showCrossOrg && (
                                <div className="mt-12">
                                    <button
                                        type="button"
                                        onClick={() => setShowCrossOrg(true)}
                                        className="flex items-center gap-8 text-sm font-medium text-org-blue-primary hover:text-org-blue-primary/80 transition-colors"
                                    >
                                        <IconLink size={15} />
                                        Assign Cross Organization
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Financial Information */}
                    <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="bg-org-blue-primary px-16 py-8">
                            <div className="flex items-center gap-12">
                                <div className="w-40 h-40 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/10">
                                    <IconCreditCard size={22} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white leading-tight">Financial Information</h3>
                                    <p className="text-white text-xs">Bank, pension, and tax details</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-16 bg-white">
                            {/* Bank Details */}
                            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-10">Bank Details</h4>
                            <div className="grid md:grid-cols-12 gap-20 mb-20">
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Bank Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.bank_name}
                                        onChange={(e) => updateField('bank_name', e.target.value)}
                                        placeholder="e.g., First Bank of Nigeria"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('bank_name') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('bank_name') && <span className="text-danger text-xs mt-4 block">{errors.bank_name}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Account Number <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.bank_account_number}
                                        onChange={(e) => updateField('bank_account_number', e.target.value)}
                                        placeholder="e.g., 3012345678"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('bank_account_number') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('bank_account_number') && <span className="text-danger text-xs mt-4 block">{errors.bank_account_number}</span>}
                                </div>
                                <div className="form-group md:col-span-4">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Account Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.bank_account_name}
                                        onChange={(e) => updateField('bank_account_name', e.target.value)}
                                        placeholder="e.g., John Doe"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('bank_account_name') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('bank_account_name') && <span className="text-danger text-xs mt-4 block">{errors.bank_account_name}</span>}
                                </div>
                            </div>

                            {/* Pension Details */}
                            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-10">Pension Details</h4>
                            <div className="grid md:grid-cols-12 gap-20 mb-20">
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">PFA Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.pfa_name}
                                        onChange={(e) => updateField('pfa_name', e.target.value)}
                                        placeholder="e.g., Stanbic IBTC Pension Managers"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('pfa_name') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('pfa_name') && <span className="text-danger text-xs mt-4 block">{errors.pfa_name}</span>}
                                </div>
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">PFA Number <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.pfa_number}
                                        onChange={(e) => updateField('pfa_number', e.target.value)}
                                        placeholder="e.g., PEN100000000001"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('pfa_number') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('pfa_number') && <span className="text-danger text-xs mt-4 block">{errors.pfa_number}</span>}
                                </div>
                            </div>

                            {/* Tax Details */}
                            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-10">Tax Details</h4>
                            <div className="grid md:grid-cols-12 gap-20">
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Tax Identification Number</label>
                                    <input
                                        type="text"
                                        value={formData.tax_identification_number}
                                        onChange={(e) => updateField('tax_identification_number', e.target.value)}
                                        placeholder="e.g., 9876543-0001"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('tax_identification_number') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('tax_identification_number') && <span className="text-danger text-xs mt-4 block">{errors.tax_identification_number}</span>}
                                </div>
                                <div className="form-group md:col-span-6">
                                    <label className="font-semibold text-gray-700 mb-6 block text-sm">Tax Authority</label>
                                    <input
                                        type="text"
                                        value={formData.tax_authority}
                                        onChange={(e) => updateField('tax_authority', e.target.value)}
                                        placeholder="e.g., FCT-IRS"
                                        className={`w-full px-12 py-10 rounded-lg border transition-all ${hasError('tax_authority') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                    />
                                    {hasError('tax_authority') && <span className="text-danger text-xs mt-4 block">{errors.tax_authority}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Account Setup — create mode only */}
                    {!isEditMode && (
                        <div className="bg-bg-dark rounded-xl shadow-sm border border-border overflow-hidden">
                            <div className="bg-org-blue-primary px-16 py-8">
                                <div className="flex items-center gap-12">
                                    <div className="w-40 h-40 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/10">
                                        <IconLock size={22} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-white leading-tight">Password Setup</h3>
                                        <p className="text-white text-xs">Set a password for this employee's account</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-16 bg-white">
                                <div className="grid md:grid-cols-12 gap-20">
                                    <div className="form-group md:col-span-6">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                            Password <span className="text-danger">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.password}
                                                onChange={(e) => updateField('password', e.target.value)}
                                                placeholder="Min. 8 characters"
                                                autoComplete="new-password"
                                                className={`w-full px-12 py-10 pr-40 rounded-lg border transition-all ${hasError('password') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(p => !p)}
                                                className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                                            </button>
                                        </div>
                                        {hasError('password') && <span className="text-danger text-xs mt-4 block">{errors.password}</span>}
                                    </div>

                                    <div className="form-group md:col-span-6">
                                        <label className="font-semibold text-gray-700 mb-6 block text-sm">
                                            Confirm Password <span className="text-danger">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={formData.confirm_password}
                                                onChange={(e) => updateField('confirm_password', e.target.value)}
                                                placeholder="Re-enter password"
                                                autoComplete="new-password"
                                                className={`w-full px-12 py-10 pr-40 rounded-lg border transition-all ${hasError('confirm_password') ? 'border-danger bg-danger/5' : 'border-gray-200 focus:border-org-blue-primary focus:ring-2 focus:ring-org-blue-primary/10'}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(p => !p)}
                                                className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                {showConfirmPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                                            </button>
                                        </div>
                                        {hasError('confirm_password') && <span className="text-danger text-xs mt-4 block">{errors.confirm_password}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Form Actions */}
                    <div className="flex items-center justify-end gap-10">
                        <Link to="/employee" className="btn btn-outline-secondary">
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            className="btn btn-primary flex items-center gap-8"
                            disabled={isSubmitting}
                        >
                            {isSubmitting && <IconLoader2 size={18} className="animate-spin" />}
                            {isEditMode ? 'Update Employee' : 'Create Employee'}
                        </button>
                    </div>
                </div>
            </form>
        </>
    )
}
